import React, { useMemo, useState } from 'react'
import { Collapse } from '@mui/material'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import CheckIcon from '@mui/icons-material/Check'
import styled, { keyframes, css } from 'styled-components'
import DetailsUnitTable from 'features/bpDetails/Components/units/DetailsUnitTable'
import Modal from 'components/atoms/Modal'
import { useDispatch, useSelector } from 'react-redux'
import { selectBpUnits } from 'ducks/units/selectors'
import { addBpUnitsRoutine, removeBpUnitsWithoutUprnRoutine } from 'ducks/units/actions'
import { hasMultipleUnits } from 'utils/units'
import { isEmpty } from 'ramda'

const getUnitBuildingType = unit => unit?.buildingType || unit?.building_type || ''

const DetailsUnitSection = ({ open, section, toggleOpen, bp, sectionsCount, redirectPath, onRequestAddUnits, editMode = false, onToggleEditMode }) => {
  const bpUnits = useSelector(selectBpUnits)
  const dispatch = useDispatch()
  const [deleteWithoutUprnModalOpen, setDeleteWithoutUprnModalOpen] = useState(false)
  const isSoleResidential = section.buildingType === 'residential.dwelling.sole_unit'

  const units = useMemo(() => {
    return bpUnits
      ? bpUnits.filter(unit => getUnitBuildingType(unit) === section.buildingType)
      : []
  }, [bpUnits, section.buildingType])

  const hasFullAccess = bp.hasAccessToBuildingPassport
  const unitsWithoutUprnCount = useMemo(
    () => units.filter(unit => !String(unit?.uprn || unit?.UPRN || '').trim()).length,
    [units]
  )

  const handleAddUnit = () => {
    dispatch(addBpUnitsRoutine({
      buildingType: section.buildingType,
      amount: 1,
      bpId: bp.id
    }))
  }

  const isSoleUnit = useMemo(() => {
    if (section) {
      return !hasMultipleUnits(section.buildingType)
    } else {
      return false
    }
  }, [section])

  const handleDeleteUnitsWithoutUprn = () => {
    dispatch(removeBpUnitsWithoutUprnRoutine({
      bpId: bp.id
    }))
    setDeleteWithoutUprnModalOpen(false)
  }

  return !isEmpty(units) ? (
    <>
      <SectionWrapper>
        <SectionHeader
          isClickable={sectionsCount > 1}
          onClick={sectionsCount > 1 ? toggleOpen : () => {}}
          isOpen={open}
        >
          <HeaderLeft>
            <HeaderInfo>
              <SectionTitle>{section.name}</SectionTitle>
              {section.multi && (
                <UnitsCount>{units.length} {units.length === 1 ? 'unit' : 'units'}</UnitsCount>
              )}
            </HeaderInfo>
          </HeaderLeft>

          {sectionsCount > 1 && (
            <ExpandButton isOpen={open}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </ExpandButton>
          )}
        </SectionHeader>

        <Collapse in={open}>
          <SectionContent>
            <ContentHeader>
              <UnitsBadge>
                {section.createMulti ? `${units.length} units` : '1 unit'}
              </UnitsBadge>
              {editMode && (
                <AutosaveHint>
                  Editing: changes save automatically when you leave a field. Enter jumps to the next unit, Esc cancels.
                </AutosaveHint>
              )}
              <HeaderActions>
                {hasFullAccess && !isSoleUnit && !isSoleResidential && typeof onToggleEditMode === 'function' && (
                  <EditUnitsButton
                    type='button'
                    $active={editMode}
                    onClick={onToggleEditMode}
                    aria-pressed={editMode}
                    title={editMode ? 'Finish editing' : 'Edit unit names, UPRNs, plots, floors and areas directly in the table'}
                    data-testid='units-edit-toggle'
                  >
                    {editMode ? <CheckIcon /> : <EditOutlinedIcon />}
                    <span>{editMode ? 'Done editing' : 'Edit units'}</span>
                  </EditUnitsButton>
                )}
                {hasFullAccess && !isSoleUnit && !editMode && unitsWithoutUprnCount > 0 && units.length > 1 && (
                <DeleteNoUprnButton type="button" onClick={() => setDeleteWithoutUprnModalOpen(true)}>
                  <DeleteSweepIcon />
                  <span>
                    Delete no-UPRN units ({unitsWithoutUprnCount})
                  </span>
                </DeleteNoUprnButton>
              )}
              </HeaderActions>
            </ContentHeader>

            {units && (
              <TableContainer>
                <DetailsUnitTable
                  section={section}
                  units={units}
                  isSoleUnit={isSoleUnit}
                  isSoleResidential={isSoleResidential}
                  redirectPath={redirectPath}
                  onRequestAddUnits={onRequestAddUnits}
                  editMode={editMode}
                />
              </TableContainer>
            )}

            {!isSoleUnit && hasFullAccess && (
              <AddUnitButton onClick={handleAddUnit}>
                <AddCircleOutlineIcon className="add-icon" />
                <span className="add-text">Add one unit</span>
              </AddUnitButton>
            )}
          </SectionContent>
        </Collapse>
      </SectionWrapper>

      <Modal
        open={deleteWithoutUprnModalOpen}
        title='Delete units without UPRN'
        onClose={() => setDeleteWithoutUprnModalOpen(false)}
        minWidthDesktop='420px'
        maxWidthDesktop='520px'
      >
        <DeleteNoUprnModalBody>
          <DeleteNoUprnIcon>
            <DeleteSweepIcon />
          </DeleteNoUprnIcon>
          <DeleteNoUprnTitle>
            Delete {unitsWithoutUprnCount} {unitsWithoutUprnCount === 1 ? 'unit' : 'units'} without UPRN?
          </DeleteNoUprnTitle>
          <DeleteNoUprnMessage>
            This will only remove units that do not have a UPRN. Units matched
            from OS data will stay in the Building Passport.
          </DeleteNoUprnMessage>
          <DeleteNoUprnActions>
            <CancelDeleteNoUprnButton
              type='button'
              onClick={() => setDeleteWithoutUprnModalOpen(false)}
            >
              Cancel
            </CancelDeleteNoUprnButton>
            <ConfirmDeleteNoUprnButton
              type='button'
              onClick={handleDeleteUnitsWithoutUprn}
            >
              Delete no-UPRN units
            </ConfirmDeleteNoUprnButton>
          </DeleteNoUprnActions>
        </DeleteNoUprnModalBody>
      </Modal>
    </>
  ) : null
}

export default DetailsUnitSection

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const breakpoints = {
  mobile: '480px',
  tablet: '768px',
  desktop: '1024px'
}

const colors = {
  darkNavy: '#0c132b',
  teal: '#36becd',
  lightBg: '#fcfbfe',
  lightBg2: '#f6f7fe',
  lightBg3: '#eef2fa',
  lightBg4: '#e8eaf5',
  lightBg5: '#cdd4ea',
  lightTeal: '#bbeaf0',
  orange: '#ffaf7f',
  orangeDark: '#fd836b',
  red: '#cd2000',
  yellow: '#ffc111'
}

const SectionWrapper = styled.div`
  animation: ${fadeIn} 0.4s ease-out;
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  margin: -24px -24px 0 -24px;
  background: ${({ isOpen }) => isOpen
    ? `linear-gradient(135deg, ${colors.lightTeal}40 0%, ${colors.lightBg3} 100%)`
    : `linear-gradient(135deg, ${colors.lightBg2} 0%, ${colors.lightBg3} 100%)`};
  border-radius: 8px 8px 0 0;
  cursor: ${({ isClickable }) => isClickable ? 'pointer' : 'default'};
  transition: all 0.3s ease;
  border-bottom: 1px solid ${colors.lightBg4};

  &:hover {
    ${({ isClickable }) => isClickable && css`
      background: linear-gradient(135deg, ${colors.lightTeal}50 0%, ${colors.lightBg4} 100%);
    `}
  }

  @media (max-width: ${breakpoints.tablet}) {
    padding: 14px 16px;
    margin: -20px -20px 0 -20px;
    border-radius: 8px 8px 0 0;
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding: 12px 14px;
    margin: -16px -16px 0 -16px;
    border-radius: 8px 8px 0 0;
  }
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;

  @media (max-width: ${breakpoints.mobile}) {
    gap: 10px;
  }
`

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const SectionTitle = styled.h3`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 17px;
  font-weight: 700;
  color: ${colors.darkNavy};
  margin: 0;
  letter-spacing: -0.3px;

  @media (max-width: ${breakpoints.tablet}) {
    font-size: 16px;
  }

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 15px;
  }
`

const UnitsCount = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 13px;
  color: ${colors.red};
  font-weight: 500;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 12px;
  }
`

const ExpandButton = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 6px;
  background: ${({ isOpen }) => isOpen ? `${colors.teal}20` : colors.lightBg4};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  svg {
    color: ${({ isOpen }) => isOpen ? colors.teal : colors.darkNavy};
    font-size: 22px;
    transition: all 0.3s ease;
  }

  @media (max-width: ${breakpoints.mobile}) {
    width: 32px;
    height: 32px;
    border-radius: 6px;

    svg {
      font-size: 20px;
    }
  }
`

const SectionContent = styled.div`
  padding-top: 20px;
  animation: ${fadeIn} 0.3s ease-out;

  @media (max-width: ${breakpoints.tablet}) {
    padding-top: 16px;
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding-top: 14px;
  }
`

const ContentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;

  @media (max-width: ${breakpoints.mobile}) {
    margin-bottom: 12px;
  }
`

const UnitsBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background: linear-gradient(135deg, ${colors.lightBg3} 0%, ${colors.lightBg4} 100%);
  border-radius: 4px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.darkNavy};
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 5px 10px;
    font-size: 11px;
  }
`

const AutosaveHint = styled.span`
  flex: 1;
  min-width: 200px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 12px;
  color: #5f6678;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;

  @media (max-width: ${breakpoints.mobile}) {
    width: 100%;
  }
`

const EditUnitsButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 14px;
  border: 1px solid ${colors.teal};
  border-radius: 6px;
  background: ${({ $active }) => ($active ? colors.teal : '#fff')};
  color: ${({ $active }) => ($active ? '#fff' : colors.teal)};
  cursor: pointer;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 12px;
  font-weight: 700;
  transition: all 0.2s ease;

  svg {
    font-size: 18px;
  }

  &:hover {
    background: ${({ $active }) => ($active ? '#2aa9b8' : '#ecfeff')};
  }

  @media (max-width: ${breakpoints.tablet}) {
    display: none;
  }
`

const DeleteNoUprnButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 12px;
  border: 1px solid ${colors.lightBg5};
  border-radius: 6px;
  background: #fff;
  color: ${colors.red};
  cursor: pointer;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 12px;
  font-weight: 700;
  transition: all 0.2s ease;

  svg {
    font-size: 18px;
  }

  &:hover {
    border-color: ${colors.red};
    background: #fff5f2;
  }

  @media (max-width: ${breakpoints.mobile}) {
    width: 100%;
    justify-content: center;
  }
`

const DeleteNoUprnModalBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
`

const DeleteNoUprnIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: #fff5f2;
  color: ${colors.red};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 24px;
  }
`

const DeleteNoUprnTitle = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${colors.darkNavy};
  line-height: 1.3;
`

const DeleteNoUprnMessage = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: #5f6678;
`

const DeleteNoUprnActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  width: 100%;
  padding-top: 8px;

  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column-reverse;
  }
`

const CancelDeleteNoUprnButton = styled.button`
  padding: 10px 16px;
  border: 1px solid ${colors.lightBg5};
  border-radius: 6px;
  background: #fff;
  color: ${colors.darkNavy};
  cursor: pointer;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 13px;
  font-weight: 700;

  &:hover {
    background: ${colors.lightBg2};
  }

  @media (max-width: ${breakpoints.mobile}) {
    width: 100%;
  }
`

const ConfirmDeleteNoUprnButton = styled.button`
  padding: 10px 16px;
  border: 1px solid ${colors.red};
  border-radius: 6px;
  background: ${colors.red};
  color: #fff;
  cursor: pointer;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 13px;
  font-weight: 700;

  &:hover {
    background: #a91d05;
    border-color: #a91d05;
  }

  @media (max-width: ${breakpoints.mobile}) {
    width: 100%;
  }
`

const TableContainer = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin: 0 -8px;
  padding: 0 8px;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${colors.lightBg3};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.lightBg5};
    border-radius: 3px;

    &:hover {
      background: ${colors.cdd4ea};
    }
  }
`

const AddUnitButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 12px 18px;
  background: transparent;
  border: 2px dashed ${colors.lightBg5};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.25s ease;
  width: 100%;
  justify-content: center;

  .add-icon {
    color: ${colors.cdd4ea};
    font-size: 22px;
    transition: all 0.25s ease;
  }

  .add-text {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.cdd4ea};
    text-transform: uppercase;
    letter-spacing: 0.3px;
    transition: all 0.25s ease;
  }

  &:hover {
    border-color: ${colors.teal};
    background: ${colors.lightTeal}20;

    .add-icon {
      color: ${colors.teal};
    }

    .add-text {
      color: ${colors.teal};
    }
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: ${breakpoints.tablet}) {
    padding: 10px 16px;
    margin-top: 14px;

    .add-icon {
      font-size: 20px;
    }

    .add-text {
      font-size: 13px;
    }
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding: 10px 14px;
    margin-top: 12px;
    border-radius: 6px;

    .add-icon {
      font-size: 18px;
    }

    .add-text {
      font-size: 12px;
    }
  }
`
